import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { FileText, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";

interface Props {
  /** ⌘K fallback opens the full command palette. */
  onOpenCommandPalette: () => void;
  placeholder?: string;
}

/**
 * TopSearchInput — incremental as-you-type search in the topbar.
 *
 * Pattern: input live + debounced 200ms call to /v2/search → renders a
 * dropdown of up to 8 results directly below the input. Arrow keys
 * navigate, Enter opens, Escape closes, ⌘K opens the full palette.
 */
export function TopSearchInput({ onOpenCommandPalette, placeholder }: Props) {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  // Debounce 200ms
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQ(q.trim()), 200);
    return () => window.clearTimeout(id);
  }, [q]);

  const results = useQuery({
    queryKey: ["top-search", debouncedQ],
    queryFn: () =>
      api.search({ query: debouncedQ, topK: 8 }).catch(() => ({ items: [] as unknown[] }) as never),
    enabled: debouncedQ.length >= 2,
    staleTime: 30_000,
  });

  const items = ((results.data as { items?: unknown[] })?.items ?? []) as Array<{
    id: string;
    doc_id: string;
    title: string;
    path: string;
    excerpt: string;
    heading_path: string[];
    score: number;
    section_id: string | null;
  }>;

  // Close on outside click
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  function openResult(idx: number) {
    const r = items[idx];
    if (!r) return;
    setOpen(false);
    setQ("");
    void navigate({ to: "/docs/$docId", params: { docId: r.doc_id } });
  }

  return (
    <div
      ref={wrapRef}
      data-testid="top-search"
      className="relative flex h-9 min-w-0 max-w-[560px] flex-1 items-center gap-2"
    >
      <div className="flex w-full items-center gap-2 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-3 transition-colors focus-within:bg-(--bg-elevated) focus-within:ring-2 focus-within:ring-(--accent-300)">
        <Search size={14} className="text-(--text-tertiary)" />
        <input
          ref={inputRef}
          data-testid="top-search-input"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(q.length > 0)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setOpen(true);
              setActive((i) => Math.min(items.length - 1, i + 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((i) => Math.max(0, i - 1));
            } else if (e.key === "Enter") {
              if (items.length > 0) {
                e.preventDefault();
                openResult(active);
              }
            } else if (e.key === "Escape") {
              setOpen(false);
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder={placeholder ?? "Buscar docs, secciones…"}
          className="h-full flex-1 bg-transparent text-sm text-(--text-primary) outline-none placeholder:text-(--text-tertiary)"
        />
        <button
          type="button"
          onClick={onOpenCommandPalette}
          aria-label="Abrir paleta de comandos (⌘K)"
          className="rounded border border-(--border-subtle) bg-(--bg-elevated) px-1.5 py-0.5 text-[10px] font-mono text-(--text-tertiary) hover:text-(--text-primary)"
        >
          ⌘K
        </button>
      </div>

      {open && debouncedQ.length >= 2 && (
        <div
          data-testid="top-search-dropdown"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[440px] overflow-y-auto rounded-md border border-(--border-default) bg-(--bg-elevated) shadow-(--elev-3)"
        >
          {results.isLoading && (
            <p className="px-3 py-2 text-xs text-(--text-tertiary)">Buscando…</p>
          )}
          {!results.isLoading && items.length === 0 && (
            <p data-testid="top-search-empty" className="px-3 py-2 text-xs text-(--text-tertiary)">
              Sin resultados para <strong>"{debouncedQ}"</strong>
            </p>
          )}
          {items.map((r, idx) => (
            <button
              key={r.id}
              type="button"
              onClick={() => openResult(idx)}
              onMouseEnter={() => setActive(idx)}
              data-testid="top-search-result"
              data-active={idx === active ? "true" : "false"}
              className={`flex w-full items-start gap-2 border-b border-(--border-subtle) px-3 py-2 text-left text-xs last:border-b-0 transition ${
                idx === active ? "bg-(--bg-hover)" : "hover:bg-(--bg-hover)"
              }`}
            >
              <FileText size={12} className="mt-0.5 text-(--text-tertiary)" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-(--text-primary)">{r.title}</p>
                <p className="truncate font-mono text-[10px] text-(--text-tertiary)">
                  {r.path}
                  {r.heading_path.length > 0 && <> · § {r.heading_path.join(" › ")}</>}
                </p>
                <p
                  className="mt-0.5 line-clamp-2 text-[11px] text-(--text-secondary)"
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: excerpt comes from server-controlled retrieval
                  dangerouslySetInnerHTML={{ __html: r.excerpt }}
                />
              </div>
              <span className="shrink-0 font-mono text-[10px] text-(--text-tertiary)">
                {r.score.toFixed(2)}
              </span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              void navigate({ to: "/search" });
            }}
            data-testid="top-search-see-all"
            className="block w-full px-3 py-2 text-center text-[11px] text-(--accent-600) hover:bg-(--bg-hover)"
          >
            Ver todos los resultados →
          </button>
        </div>
      )}
    </div>
  );
}
